import React, { useState } from "react";
import { toast } from "react-hot-toast";
import Swal from "sweetalert2";

const claimStatusStyles = {
  NULL: "bg-gray-100 text-gray-700",
  "For Processing": "bg-teal-100 text-teal-700",
  Processed: "bg-green-100 text-green-700",
  Extracted: "bg-yellow-100 text-yellow-700",
  Incomplete: "bg-red-100 text-red-700",
  Paid: "bg-indigo-100 text-indigo-700",
};

// Batch Details Component
const BatchDetails = ({ formData, phpFormatter }) => (
  <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
    <h3 className="text-md font-semibold text-gray-900 mb-4">Batch Details</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
      {[
        { label: "Batch Number", value: formData.batch_no },
        { label: "Batch Status", value: formData.batch_status },
        { label: "Date Received", value: formData.received_date },
        { label: "Due Date", value: formData.due_date },
        { label: "Provider Code", value: formData.provider_code },
        { label: "Hospital Reference No", value: formData.provider_reference_no },
        {
          label: "SOA Amount",
          value: phpFormatter.format(Number(formData.soa_amount) || 0),
        },
        { label: "Accepted By", value: formData.accepted_by },
        { label: "Actual Claim Count", value: formData.actual_claim_count },
        {
          label: "Actual Claim Amount",
          value: phpFormatter.format(formData.actual_claim_amount),
        },
        { label: "Remarks", value: formData.remarks || "None" },
      ].map((item) => (
        <div key={item.label} className="flex justify-between py-1">
          <span className="font-medium text-gray-600">{item.label}:</span>
          <span className="text-gray-900 break-words">{item.value}</span>
        </div>
      ))}
    </div>
  </div>
);

// SOA Files Table Component
const SoaFilesTable = ({ soaFiles, handleViewFile, handleRefreshLink }) => {
  const [isOpen, setIsOpen] = useState(true); // Changed to true to show files by default

  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-md font-semibold text-gray-900">Statement of Account (SOA) Files</h3>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="text-gray-500 hover:text-gray-700 hover:scale-110 transition-all duration-200"
          aria-label={isOpen ? "Collapse SOA Files" : "Expand SOA Files"}
        >
          <i className={`fas ${isOpen ? "fa-chevron-up" : "fa-chevron-down"}`}></i>
        </button>
      </div>
      {isOpen && (
        <div className="mt-2">
          {!soaFiles || !Array.isArray(soaFiles) ? (
            <p className="text-red-600 text-sm">
              Error: Invalid SOA files data. Expected an array.
            </p>
          ) : soaFiles.length === 0 ? (
            <p className="text-gray-500 text-sm">No SOA files attached.</p>
          ) : (
            <table className="w-full table-auto">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">File Name</th>
                  <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
                  <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {soaFiles.map((fileObj, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="p-3 text-sm truncate">
                      {fileObj?.file?.name || "Unknown File"}
                    </td>
                    <td className="p-3 text-sm">
                      {fileObj?.uploadId ? (
                        <span className="text-teal-600">Uploaded (ID: {fileObj.uploadId})</span>
                      ) : (
                        <span className="text-gray-500">Pending</span>
                      )}
                    </td>
                    <td className="p-3 text-sm">
                      <div className="flex items-center gap-3">
                        {fileObj?.signed_url ? (
                          <button
                            onClick={() => handleViewFile(fileObj)}
                            className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                            title="View file"
                          >
                            <i className="fas fa-eye"></i> View
                          </button>
                        ) : (
                          <span className="text-gray-500">No URL</span>
                        )}
                        {fileObj?.uploadId && (
                          <button
                            onClick={() => handleRefreshLink(fileObj, "soa")}
                            className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                            title="Refresh link"
                          >
                            <i className="fas fa-sync-alt"></i> Refresh Link
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

// Claim Files Modal Component
const ClaimFilesModal = ({ isOpen, onClose, claim, handleViewFile, handleRefreshLink }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] backdrop-blur-sm">
      <div className="bg-white rounded-lg p-6 w-[90vw] max-w-4xl max-h-[80vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">
            Files for Claim {claim.loa_no} - {claim.transaction_no}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 hover:scale-110 transition-all duration-200"
            aria-label="Close modal"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        <table className="w-full table-auto">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">File Name</th>
              <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {claim.attachedFiles && claim.attachedFiles.length > 0 ? (
              claim.attachedFiles.map((fileObj, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="p-3 text-sm truncate">{fileObj.file.name}</td>
                  <td className="p-3 text-sm">
                    {fileObj.uploadId ? (
                      <span className="text-teal-600">Uploaded (ID: {fileObj.uploadId})</span>
                    ) : (
                      <span className="text-gray-500">Pending</span>
                    )}
                  </td>
                  <td className="p-3 text-sm">
                    <div className="flex items-center gap-3">
                      {fileObj.signed_url && (
                        <button
                          onClick={() => handleViewFile(fileObj)}
                          className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                          title="View file"
                        >
                          <i className="fas fa-eye"></i> View
                        </button>
                      )}
                      {fileObj.uploadId && (
                        <button
                          onClick={() => handleRefreshLink(fileObj, "claim")}
                          className="text-blue-600 hover:text-blue-800 flex items-center gap-1"
                          title="Refresh link"
                        >
                          <i className="fas fa-sync-alt"></i> Refresh Link
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" className="p-3 text-center text-gray-500 text-sm">
                  No files attached.
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="mt-4 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 hover:scale-105 transition-all duration-200 flex items-center gap-2"
            aria-label="Close modal"
          >
            <i className="fas fa-times"></i> Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Pagination Component
const Pagination = ({ currentPage, totalPages, itemsPerPage, totalItems, onPageChange, onItemsPerPageChange }) => {
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  return (
    <div className="flex items-center justify-between mt-4">
      <div className="text-sm text-gray-600">
        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, totalItems)} of {totalItems} entries
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded-md bg-gray-200 text-gray-600 disabled:opacity-50 hover:bg-gray-300"
        >
          <i className="fas fa-angle-double-left"></i> First
        </button>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 rounded-md bg-gray-200 text-gray-600 disabled:opacity-50 hover:bg-gray-300"
        >
          <i className="fas fa-angle-left"></i> Previous
        </button>
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          const page = currentPage - 2 + i;
          if (page > 0 && page <= totalPages) {
            return (
              <button
                key={page}
                onClick={() => onPageChange(page)}
                className={`px-3 py-1 rounded-md ${
                  currentPage === page ? "bg-indigo-600 text-white" : "bg-gray-200 text-gray-600 hover:bg-gray-300"
                }`}
              >
                {page}
              </button>
            );
          }
          return null;
        })}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded-md bg-gray-200 text-gray-600 disabled:opacity-50 hover:bg-gray-300"
        >
          Next <i className="fas fa-angle-right"></i>
        </button>
        <button
          onClick={() => onPageChange(totalPages)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 rounded-md bg-gray-200 text-gray-600 disabled:opacity-50 hover:bg-gray-300"
        >
          Last <i className="fas fa-angle-double-right"></i>
        </button>
        <select
          value={itemsPerPage}
          onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
          className="px-3 py-1 rounded-md bg-gray-200 text-gray-600"
        >
          <option value={10}>10</option>
          <option value={25}>25</option>
 Vista 50
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
      </div>
    </div>
  );
};

// Claims Table Component
const ClaimsTable = ({ attachedClaims, phpFormatter, handleViewFile, handleRefreshLink }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedClaim, setSelectedClaim] = useState(null);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentClaims = attachedClaims.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(attachedClaims.length / itemsPerPage);

  return (
    <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
      <h3 className="text-md font-semibold text-gray-900 mb-4">Attached Claims</h3>
      <table className="w-full table-auto">
        <thead className="bg-gray-50">
          <tr>
            <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Loa No.</th>
            <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Transaction No.</th>
            <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
            <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Patient Name</th>
            <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Coverage</th>
            <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
            <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Claim Status</th>
            <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Stale?</th>
            <th className="p-3 text-left text-xs font-semibold text-gray-600 uppercase">Files</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {currentClaims.length === 0 ? (
            <tr>
              <td colSpan="9" className="p-3 text-center text-gray-500 text-sm">
                No claims attached.
              </td>
            </tr>
          ) : (
            currentClaims.map((claim) => (
              <tr key={claim.id} className="hover:bg-gray-50">
                <td className="p-3 text-sm">{claim.loa_no}</td>
                <td className="p-3 text-sm">{claim.transaction_no}</td>
                <td className="p-3 text-sm">{phpFormatter.format(claim.amount)}</td>
                <td className="p-3 text-sm">{claim.patient_name}</td>
                <td className="p-3 text-sm">{claim.coverage}</td>
                <td className="p-3 text-sm">{claim.date}</td>
                <td className="p-3 text-sm">
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${
                      claimStatusStyles[claim.claim_status] || claimStatusStyles["NULL"]
                    }`}
                  >
                    {claim.claim_status || "NULL"}
                  </span>
                </td>
                <td className="p-3 text-sm">{claim.stale ? "Yes" : "No"}</td>
                <td className="p-3 text-sm">
                  <button
                    onClick={() => setSelectedClaim(claim)}
                    className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                    title="View files"
                  >
                    <i className="fas fa-eye"></i> View Files
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        totalItems={attachedClaims.length}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={(value) => {
          setItemsPerPage(value);
          setCurrentPage(1);
        }}
      />
      <ClaimFilesModal
        isOpen={!!selectedClaim}
        onClose={() => setSelectedClaim(null)}
        claim={selectedClaim || {}}
        handleViewFile={handleViewFile}
        handleRefreshLink={handleRefreshLink}
      />
    </div>
  );
};

// Main Confirm Submit Modal Component
const ConfirmSubmitModal = ({
  isOpen,
  onClose,
  onConfirm,
  formData,
  soaFiles,
  attachedClaims,
  phpFormatter,
  isSubmitting,
  handleRefreshLink,
}) => {
  if (!isOpen) return null;

  const claimsWithoutFiles = attachedClaims.filter(
    (claim) => !claim.attachedFiles || claim.attachedFiles.length === 0
  );
  const isValid = claimsWithoutFiles.length === 0;

  const handleViewFile = (fileObj) => {
    if (fileObj.signed_url) {
      window.open(fileObj.signed_url, "_blank");
    } else {
      toast.error("File URL not available.", { duration: 4000 });
    }
  };

  const handleConfirmClick = async () => {
    const result = await Swal.fire({
      title: "Confirm Submission",
      text: "Are you sure you want to submit this batch? This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#4f46e5",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, Submit",
      cancelButtonText: "Cancel",
      customClass: {
        popup: "rounded-xl shadow-lg",
        title: "text-lg font-semibold text-gray-900",
        content: "text-sm text-gray-600",
        confirmButton: "px-4 py-2 rounded-md text-white bg-indigo-600 hover:bg-indigo-700",
        cancelButton: "px-4 py-2 rounded-md text-white bg-gray-500 hover:bg-gray-600",
      },
    });

    if (result.isConfirmed) {
      onConfirm();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 backdrop-blur-sm">
      {isSubmitting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white/90 rounded-xl p-6 flex flex-col items-center space-y-4 animate-pulse shadow-lg">
            <i className="fas fa-spinner text-4xl text-indigo-600 animate-spin"></i>
            <p className="text-lg font-medium text-gray-800">Submitting Batch...</p>
            <p className="text-sm text-gray-500">This may take a moment. Please wait.</p>
          </div>
        </div>
      )}
      <div className="bg-white/90 rounded-xl shadow-lg w-[95vw] max-w-1xl h-[90vh] overflow-y-auto">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white/95 z-10">
          <h2 className="text-lg font-semibold text-gray-900">Confirm Batch Submission</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 hover:scale-110 transition-all duration-200"
            aria-label="Close modal"
            disabled={isSubmitting}
          >
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="p-6 space-y-6">
          <BatchDetails formData={formData} phpFormatter={phpFormatter} />
          <SoaFilesTable
            soaFiles={soaFiles}
            handleViewFile={handleViewFile}
            handleRefreshLink={handleRefreshLink}
          />
          <ClaimsTable
            attachedClaims={attachedClaims}
            phpFormatter={phpFormatter}
            handleViewFile={handleViewFile}
            handleRefreshLink={handleRefreshLink}
          />
        </div>
        <div className="p-4 border-t border-gray-200 flex flex-col gap-3 sticky bottom-0 bg-white/95 z-10">
          {!isValid && (
            <div className="text-red-600 text-sm font-medium">
              <span className="font-semibold">Error:</span> All claims must have at least one
              attached file. The following claims are missing files:
              <ul className="list-disc list-inside mt-1">
                {claimsWithoutFiles.map((claim) => (
                  <li key={claim.id} className="break-words">
                    Claim {claim.loa_no}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 hover:scale-105 transition-all duration-200 flex items-center gap-2"
              disabled={isSubmitting}
              aria-label="Close modal"
            >
              <i className="fas fa-times"></i> Close
            </button>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 hover:scale-105 transition-all duration-200"
              disabled={isSubmitting}
              aria-label="Cancel"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmClick}
              className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-teal-600 text-white rounded-md hover:from-indigo-700 hover:to-teal-700 hover:scale-105 flex items-center gap-2 disabled:opacity-50 transition-all duration-200"
              disabled={isSubmitting || !isValid}
              aria-label="Confirm submit"
            >
              <i className="fas fa-save"></i>
              {isSubmitting ? "Submitting..." : "Confirm Submit"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmSubmitModal;
